<script setup>
// eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text
import { ref, reactive, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import nexceoAiEmployeesAPI from 'dashboard/api/nexceo/aiEmployees';
import nexceoProjectsAPI from 'dashboard/api/nexceo/projects';
import nexceoSettingsAPI from 'dashboard/api/nexceo/settings';
import ProjectWorkspaceLayout from './ProjectWorkspaceLayout.vue';
import { projectWorkspaceTabs } from './projectWorkspaceTabs';

const route = useRoute();
const router = useRouter();
const { accountId, accountScopedRoute } = useAccount();

const workspaceTabs = computed(() =>
  projectWorkspaceTabs({
    standalone: true,
    id: route.params.id,
    accountId: accountId.value,
  })
);

const isLoading = ref(false);
const isSaving = ref(false);
const error = ref(null);
const success = ref(false);
const projectData = ref(null);
const handoffTriggersRaw = ref('');
const newHandoffTrigger = ref('');

// AI Test Panel
const testInput = ref('');
const isTesting = ref(false);
const testError = ref('');
const testResult = ref(null);
const showSystemPrompt = ref(false);

// Company name loaded from NEXCEO Settings for preview/warnings
const companyName = ref('');

const SAMPLE_MESSAGES = [
  { label: 'Asks for brochure', message: 'Please send the brochure for 3BHK.' },
  { label: 'Asks for price sheet', message: 'Can you send the price sheet?' },
  {
    label: 'Asks for site visit',
    message: 'I want to visit tomorrow afternoon, say 4 PM.',
  },
  {
    label: 'Who are you?',
    message: 'Who are you? Are you from NEXCEO?',
  },
  {
    label: 'Asks about company',
    message: 'Tell me about your company.',
  },
];

async function runTest() {
  const msg = testInput.value.trim();
  if (!msg) return;
  isTesting.value = true;
  testError.value = '';
  testResult.value = null;
  showSystemPrompt.value = false;
  try {
    const { data } = await nexceoAiEmployeesAPI.testMessage(
      route.params.id,
      msg
    );
    testResult.value = data;
  } catch (e) {
    testError.value =
      e?.response?.data?.error ??
      'Test failed. Check API key and model settings.';
  } finally {
    isTesting.value = false;
  }
}

function useSample(msg) {
  testInput.value = msg;
}

const form = reactive({
  name: '',
  role: 'project_sales_assistant',
  tone: 'professional',
  status: 'active',
  model: '',
  system_prompt: '',
  language_preferences: ['en', 'hi'],
  language_mode: 'auto',
  auto_reply_enabled: true,
  handoff_enabled: true,
  working_hours_enabled: false,
  max_auto_replies_per_conversation: 20,
  handoff_triggers: [],
  working_hours: {},
  settings: {},
  responsibilities: [],
  permissions: {},
  escalation_rules: {},
});

const ALL_TOOLS = [
  'update_lead',
  'move_stage',
  'share_document',
  'schedule_site_visit',
  'notify_agent',
  'assign_agent',
  'pause_ai',
  'add_private_note',
  'check_inventory',
  'reserve_unit',
  'block_unit',
];

const ROLE_TOOLS = {
  project_sales_agent: [
    'update_lead',
    'move_stage',
    'share_document',
    'schedule_site_visit',
    'notify_agent',
    'assign_agent',
    'pause_ai',
    'add_private_note',
    'check_inventory',
    'reserve_unit',
    'block_unit',
  ],
  lead_qualifier: [
    'update_lead',
    'move_stage',
    'notify_agent',
    'add_private_note',
    'pause_ai',
  ],
  site_visit_coordinator: [
    'schedule_site_visit',
    'notify_agent',
    'move_stage',
    'add_private_note',
  ],
  document_assistant: ['share_document', 'add_private_note', 'check_inventory'],
  human_handoff_assistant: [
    'notify_agent',
    'pause_ai',
    'add_private_note',
    'assign_agent',
  ],
};

const useCustomTools = ref(false);

const defaultToolsForRole = computed(
  () => ROLE_TOOLS[form.role] || ROLE_TOOLS.project_sales_agent
);

const customToolsList = computed({
  get() {
    const t = form.permissions?.allowed_tools;
    return Array.isArray(t) ? [...t] : [...defaultToolsForRole.value];
  },
  set(val) {
    if (!form.permissions) form.permissions = {};
    form.permissions.allowed_tools = val;
  },
});

function toggleTool(tool) {
  const current = customToolsList.value;
  const idx = current.indexOf(tool);
  customToolsList.value =
    idx === -1 ? [...current, tool] : current.filter(t => t !== tool);
}

function resetToRoleDefaults() {
  if (!form.permissions) form.permissions = {};
  form.permissions.allowed_tools = [];
  useCustomTools.value = false;
}

const effectiveTools = computed(() => {
  if (useCustomTools.value) return customToolsList.value;
  const custom = form.permissions?.allowed_tools;
  return Array.isArray(custom) && custom.length > 0
    ? custom
    : defaultToolsForRole.value;
});

const usingCustomTools = computed(() => {
  const custom = form.permissions?.allowed_tools;
  return Array.isArray(custom) && custom.length > 0;
});

const escalationTriggers = computed(() =>
  Array.isArray(form.escalation_rules?.triggers)
    ? form.escalation_rules.triggers
    : []
);

const TONES = ['professional', 'friendly', 'formal', 'casual'];
const STATUSES = ['active', 'paused', 'disabled'];
// "Primary Persona" — display style only; does not restrict capabilities.
const ROLES = [
  { value: 'project_sales_assistant', label: 'Project Sales Assistant' },
  {
    value: 'project_relationship_executive',
    label: 'Project Relationship Executive',
  },
  { value: 'project_consultant', label: 'Project Consultant' },
];

// Full capabilities shared by all project AI employees regardless of persona.
const AI_CAPABILITIES = [
  'Lead qualification and scoring',
  'Project Q&A from approved data',
  'Document sharing (brochure, floor plan, price sheet)',
  'Site visit scheduling and reminders',
  'Google Meet / video call scheduling',
  'Human handoff with agent notification',
  'AI follow-up and re-engagement',
  'Kanban stage updates',
  'Lead memory and profile updates',
];
const LANGUAGE_MODES = [
  { value: 'auto', label: 'Auto (match customer language)' },
  { value: 'english', label: 'English only' },
  { value: 'hindi', label: 'Hindi only' },
  { value: 'odia', label: 'Odia only' },
  { value: 'hinglish', label: 'Hinglish' },
];
const LANGUAGES = [
  { code: 'en', label: 'English' },
  { code: 'hi', label: 'Hindi' },
  { code: 'mr', label: 'Marathi' },
  { code: 'gu', label: 'Gujarati' },
  { code: 'ta', label: 'Tamil' },
  { code: 'te', label: 'Telugu' },
  { code: 'kn', label: 'Kannada' },
  { code: 'bn', label: 'Bengali' },
  { code: 'or', label: 'Odia' },
];

const DEFAULT_HANDOFF_TRIGGERS = [
  'discount',
  'negotiation',
  'legal',
  'lawyer',
  'court',
  'complaint',
  'angry',
  'refund',
  'emi',
  'loan issue',
  'talk to manager',
  'human agent',
  'call me urgently',
];

// Overall AI readiness: all must be green for AI to respond
const statusChecks = computed(() => [
  {
    key: 'env',
    label: 'ENV kill switch',
    desc: 'NEXCEO_AI_ENABLED environment variable',
    ok: true, // server-side only — show as unknown but trust admin knows
    unknown: true,
  },
  {
    key: 'project',
    label: 'Project AI enabled',
    desc: `Project: ${projectData.value?.name || '…'}`,
    ok: projectData.value?.ai_enabled !== false,
    unknown: projectData.value === null,
  },
  {
    key: 'status',
    label: 'AI employee active',
    desc: `Status: ${form.status}`,
    ok: form.status === 'active',
    unknown: false,
  },
  {
    key: 'auto_reply',
    label: 'Auto-reply enabled',
    desc: 'Will send replies automatically',
    ok: form.auto_reply_enabled,
    unknown: false,
  },
]);

const overallOk = computed(() =>
  statusChecks.value.every(c => c.unknown || c.ok)
);

// Computed preview of how the AI will introduce itself to customers
const identityPreview = computed(() => {
  const name =
    form.settings?.display_name?.trim() ||
    form.name?.trim() ||
    'Sales Assistant';
  const role = form.settings?.role_title?.trim() || 'Sales Executive';
  const company = companyName.value || '[Company Name]';
  const template =
    form.settings?.intro_message_template?.trim() ||
    `Hi, I'm {{ai_name}} from {{company_name}}. I can help you with project details, brochures, and site visit scheduling.`;
  return template
    .replace(/\{\{ai_name\}\}/g, name)
    .replace(/\{\{company_name\}\}/g, company)
    .replace(/\{\{role_title\}\}/g, role);
});

async function load() {
  isLoading.value = true;
  try {
    const [empRes, projRes, settingsRes] = await Promise.all([
      nexceoAiEmployeesAPI.show(route.params.id),
      nexceoProjectsAPI.show(route.params.id),
      nexceoSettingsAPI.get(),
    ]);
    const d = empRes.data;
    projectData.value = projRes.data;
    companyName.value = settingsRes.data?.settings?.company_name || '';
    Object.assign(form, {
      name: d.name ?? '',
      role: d.role ?? 'project_sales_agent',
      tone: d.tone ?? 'professional',
      status: d.status ?? 'active',
      model: d.model ?? '',
      system_prompt: d.system_prompt ?? '',
      language_preferences: d.language_preferences ?? ['en', 'hi'],
      language_mode: d.language_mode ?? 'auto',
      auto_reply_enabled: d.auto_reply_enabled ?? true,
      handoff_enabled: d.handoff_enabled ?? true,
      working_hours_enabled: d.working_hours_enabled ?? false,
      max_auto_replies_per_conversation:
        d.max_auto_replies_per_conversation ?? 20,
      handoff_triggers: d.handoff_triggers ?? [],
      working_hours: d.working_hours ?? {},
      settings: d.settings ?? {},
      responsibilities: d.responsibilities ?? [],
      permissions: d.permissions ?? {},
      escalation_rules: d.escalation_rules ?? {},
    });
    const customTools = form.permissions?.allowed_tools;
    useCustomTools.value = Array.isArray(customTools) && customTools.length > 0;
    handoffTriggersRaw.value = form.handoff_triggers.join('\n');
  } catch {
    error.value = 'Failed to load AI employee settings.';
  } finally {
    isLoading.value = false;
  }
}

async function save() {
  isSaving.value = true;
  error.value = null;
  success.value = false;
  try {
    const permissions = useCustomTools.value
      ? { ...form.permissions, allowed_tools: customToolsList.value }
      : { ...form.permissions, allowed_tools: [] };

    await nexceoAiEmployeesAPI.update(route.params.id, {
      ai_employee: {
        ...form,
        handoff_triggers: handoffTriggersRaw.value
          .split('\n')
          .map(s => s.trim())
          .filter(Boolean),
        responsibilities: form.responsibilities,
        permissions,
        escalation_rules: form.escalation_rules,
      },
    });
    success.value = true;
    setTimeout(() => {
      success.value = false;
    }, 3000);
  } catch (e) {
    error.value = e?.response?.data?.message ?? 'Failed to save settings.';
  } finally {
    isSaving.value = false;
  }
}

function back() {
  router.push(
    accountScopedRoute('nexceo_projects_edit', { id: route.params.id })
  );
}

function toggleLanguage(lang) {
  const idx = form.language_preferences.indexOf(lang);
  if (idx === -1) form.language_preferences.push(lang);
  else if (form.language_preferences.length > 1)
    form.language_preferences.splice(idx, 1);
}

function addTrigger() {
  const t = newHandoffTrigger.value.trim();
  if (!t) return;
  const lines = handoffTriggersRaw.value
    .split('\n')
    .map(s => s.trim())
    .filter(Boolean);
  if (!lines.includes(t)) {
    lines.push(t);
    handoffTriggersRaw.value = lines.join('\n');
  }
  newHandoffTrigger.value = '';
}

function addDefaultTriggers() {
  const existing = handoffTriggersRaw.value
    .split('\n')
    .map(s => s.trim())
    .filter(Boolean);
  const merged = [...new Set([...existing, ...DEFAULT_HANDOFF_TRIGGERS])];
  handoffTriggersRaw.value = merged.join('\n');
}

const DAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
];

function getDaySchedule(day) {
  return (
    form.working_hours[day] || {
      open: '09:00',
      close: '20:00',
      closed: day === 'saturday' || day === 'sunday',
    }
  );
}

function setDayField(day, field, value) {
  if (!form.working_hours[day]) {
    form.working_hours[day] = {
      open: '09:00',
      close: '20:00',
      closed: day === 'saturday' || day === 'sunday',
    };
  }
  form.working_hours[day][field] = value;
}

function dayLabel(day) {
  return day.charAt(0).toUpperCase() + day.slice(1, 3);
}

onMounted(load);
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <ProjectWorkspaceLayout
    :project="projectData"
    :tabs="workspaceTabs"
    active-tab-key="ai_employee"
    icon="i-lucide-bot"
  >
    <template #actions>
      <span
        class="text-xs font-medium px-2 py-0.5 rounded-full border capitalize"
        :class="{
          'bg-success-50 text-success-800 border-success-200 dark:bg-success-800/20 dark:text-success-400':
            form.status === 'active',
          'bg-amber-50 text-amber-800 border-amber-200 dark:bg-amber-800/20 dark:text-amber-400':
            form.status === 'paused',
          'bg-n-alpha-2 text-n-slate-8 border-n-weak':
            form.status === 'disabled',
        }"
      >
        AI {{ form.status }}
      </span>
    </template>

    <!-- Loading -->
    <div v-if="isLoading" class="flex items-center justify-center flex-1">
      <span class="i-lucide-loader-2 size-6 animate-spin text-n-slate-7" />
    </div>

    <div v-else class="flex-1 overflow-y-auto">
      <div class="max-w-2xl mx-auto px-4 py-6 space-y-4">
        <!-- ── AI Status Dashboard ────────────────────────────── -->
        <div class="rounded-xl border border-n-weak overflow-hidden">
          <div class="px-4 py-3 border-b border-n-weak bg-n-alpha-1">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <span class="i-lucide-activity size-4 text-n-slate-9" />
                <h2
                  class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
                >
                  AI Status
                </h2>
              </div>
              <span
                class="text-xs font-semibold px-2 py-0.5 rounded-full"
                :class="
                  overallOk
                    ? 'bg-success-50 text-success-800 dark:bg-success-800/20 dark:text-success-400'
                    : 'bg-ruby-50 text-ruby-700 dark:bg-ruby-500/10 dark:text-ruby-400'
                "
              >
                {{ overallOk ? 'AI will respond' : 'AI is blocked' }}
              </span>
            </div>
          </div>
          <div class="divide-y divide-n-weak">
            <div
              v-for="check in statusChecks"
              :key="check.key"
              class="flex items-center justify-between px-4 py-2.5 transition-colors"
              :class="
                !check.unknown && !check.ok
                  ? 'bg-ruby-50 dark:bg-ruby-500/10 border-l-2 border-ruby-400'
                  : ''
              "
            >
              <div>
                <p class="text-sm font-medium text-n-slate-12">
                  {{ check.label }}
                </p>
                <p class="text-xs text-n-slate-8">{{ check.desc }}</p>
              </div>
              <span
                class="flex items-center gap-1.5 text-xs font-medium px-2 py-0.5 rounded-full"
                :class="
                  check.unknown
                    ? 'bg-n-alpha-2 text-n-slate-8'
                    : check.ok
                      ? 'bg-success-50 text-success-800 dark:bg-success-800/20 dark:text-success-400'
                      : 'bg-ruby-50 text-ruby-700 dark:bg-ruby-500/10 dark:text-ruby-400'
                "
              >
                <span
                  :class="
                    check.unknown
                      ? 'i-lucide-minus size-3'
                      : check.ok
                        ? 'i-lucide-check size-3'
                        : 'i-lucide-x size-3'
                  "
                />
                {{
                  check.unknown ? 'Server-side' : check.ok ? 'OK' : 'Blocked'
                }}
              </span>
            </div>
          </div>
        </div>

        <!-- Safety warning -->
        <div
          class="flex items-start gap-3 p-3.5 rounded-xl border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-800/10 text-amber-800 dark:text-amber-400 text-sm"
        >
          <span class="i-lucide-shield-alert size-4 shrink-0 mt-0.5" />
          <p>
            AI replies using <strong>approved project information only</strong>.
            Human confirmation is required for booking, discount, legal, and
            payment commitments.
          </p>
        </div>

        <!-- Error / Success -->
        <div
          v-if="error"
          class="flex items-center gap-2 p-3 rounded-lg bg-ruby-50 dark:bg-ruby-500/10 border border-ruby-200 dark:border-ruby-500/20 text-ruby-700 dark:text-ruby-400 text-sm"
        >
          <span class="i-lucide-alert-circle size-4 shrink-0" />
          {{ error }}
        </div>
        <div
          v-if="success"
          class="flex items-center gap-2 p-3 rounded-lg bg-success-50 dark:bg-success-800/20 border border-success-200 dark:border-success-500/20 text-success-800 dark:text-success-400 text-sm"
        >
          <span class="i-lucide-check-circle size-4 shrink-0" />
          Settings saved successfully.
        </div>

        <div class="rounded-xl border border-n-weak overflow-hidden">
          <!-- ── Quick Toggles ──────────────────────────────────── -->
          <div class="px-5 py-4 border-b border-n-weak">
            <h2
              class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider mb-3"
            >
              Controls
            </h2>
            <div class="flex flex-col gap-3">
              <!-- AI Status -->
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm font-medium text-n-slate-12">
                    AI Employee Active
                  </p>
                  <p class="text-xs text-n-slate-8">
                    Master on/off for this AI employee
                  </p>
                </div>
                <select
                  v-model="form.status"
                  class="text-sm px-2.5 py-1.5 rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:border-woot-500 capitalize"
                >
                  <option v-for="s in STATUSES" :key="s" :value="s">
                    {{ s }}
                  </option>
                </select>
              </div>

              <!-- Auto Reply -->
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm font-medium text-n-slate-12">
                    Auto-Reply Enabled
                  </p>
                  <p class="text-xs text-n-slate-8">
                    AI sends messages automatically
                  </p>
                </div>
                <button
                  class="relative inline-flex h-5 w-9 items-center rounded-full transition-colors"
                  :class="
                    form.auto_reply_enabled
                      ? 'bg-woot-500'
                      : 'bg-n-alpha-3 dark:bg-n-alpha-4'
                  "
                  @click="form.auto_reply_enabled = !form.auto_reply_enabled"
                >
                  <span
                    class="inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform"
                    :class="
                      form.auto_reply_enabled
                        ? 'translate-x-4'
                        : 'translate-x-0.5'
                    "
                  />
                </button>
              </div>

              <!-- Handoff Enabled -->
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm font-medium text-n-slate-12">
                    Human Handoff Enabled
                  </p>
                  <p class="text-xs text-n-slate-8">
                    AI can escalate to human agent
                  </p>
                </div>
                <button
                  class="relative inline-flex h-5 w-9 items-center rounded-full transition-colors"
                  :class="
                    form.handoff_enabled
                      ? 'bg-woot-500'
                      : 'bg-n-alpha-3 dark:bg-n-alpha-4'
                  "
                  @click="form.handoff_enabled = !form.handoff_enabled"
                >
                  <span
                    class="inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform"
                    :class="
                      form.handoff_enabled ? 'translate-x-4' : 'translate-x-0.5'
                    "
                  />
                </button>
              </div>

              <!-- Working Hours -->
              <div class="flex items-center justify-between">
                <div>
                  <p class="text-sm font-medium text-n-slate-12">
                    Working Hours Only
                  </p>
                  <p class="text-xs text-n-slate-8">
                    AI only responds during defined hours
                  </p>
                </div>
                <button
                  class="relative inline-flex h-5 w-9 items-center rounded-full transition-colors"
                  :class="
                    form.working_hours_enabled
                      ? 'bg-woot-500'
                      : 'bg-n-alpha-3 dark:bg-n-alpha-4'
                  "
                  @click="
                    form.working_hours_enabled = !form.working_hours_enabled
                  "
                >
                  <span
                    class="inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform"
                    :class="
                      form.working_hours_enabled
                        ? 'translate-x-4'
                        : 'translate-x-0.5'
                    "
                  />
                </button>
              </div>
            </div>

            <!-- Per-day schedule (shown when working hours enabled) -->
            <div v-if="form.working_hours_enabled" class="mt-3 space-y-2">
              <p class="text-xs font-medium text-n-slate-11 mb-2">
                Schedule per day
              </p>
              <div
                v-for="day in DAYS"
                :key="day"
                class="flex items-center flex-wrap gap-2 py-1.5 border-b border-n-weak last:border-0 min-h-[36px]"
              >
                <!-- Day label -->
                <span class="w-7 text-xs font-medium text-n-slate-10 shrink-0">
                  {{ dayLabel(day) }}
                </span>

                <!-- Closed toggle -->
                <button
                  class="relative inline-flex h-4 w-7 items-center rounded-full transition-colors shrink-0"
                  :class="
                    getDaySchedule(day).closed
                      ? 'bg-n-alpha-3 dark:bg-n-alpha-4'
                      : 'bg-woot-500'
                  "
                  :title="getDaySchedule(day).closed ? 'Closed' : 'Open'"
                  @click="
                    setDayField(day, 'closed', !getDaySchedule(day).closed)
                  "
                >
                  <span
                    class="inline-block h-3 w-3 rounded-full bg-white shadow transition-transform"
                    :class="
                      getDaySchedule(day).closed
                        ? 'translate-x-0.5'
                        : 'translate-x-3.5'
                    "
                  />
                </button>

                <!-- Time inputs (hidden when closed) -->
                <template v-if="!getDaySchedule(day).closed">
                  <input
                    type="time"
                    :value="getDaySchedule(day).open"
                    class="w-24 px-2 py-1 text-xs rounded border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:border-woot-500"
                    @change="setDayField(day, 'open', $event.target.value)"
                  />
                  <span class="text-xs text-n-slate-7">to</span>
                  <input
                    type="time"
                    :value="getDaySchedule(day).close"
                    class="w-24 px-2 py-1 text-xs rounded border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:border-woot-500"
                    @change="setDayField(day, 'close', $event.target.value)"
                  />
                </template>
                <span v-else class="text-xs text-n-slate-7">Closed</span>
              </div>
            </div>

            <!-- After-hours message (shown when working hours enabled) -->
            <div v-if="form.working_hours_enabled" class="mt-3">
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                After-hours message (sent once per 6h outside working hours)
              </label>
              <textarea
                v-model="form.settings.after_hours_message"
                rows="2"
                placeholder="Hi! Our office hours are 9 AM–8 PM IST. We'll get back to you shortly."
                class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-7 focus:outline-none focus:border-woot-500 resize-none"
              />
            </div>

            <!-- Max auto replies -->
            <div class="mt-3">
              <label class="block text-xs font-medium text-n-slate-11 mb-1">
                Max auto-replies per conversation
              </label>
              <input
                v-model.number="form.max_auto_replies_per_conversation"
                type="number"
                min="1"
                max="100"
                class="w-32 px-3 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 focus:outline-none focus:border-woot-500"
              />
              <p class="text-xs text-n-slate-7 mt-1">
                AI stops auto-replying after this many messages per
                conversation.
              </p>
            </div>
          </div>

          <!-- ── Identity ───────────────────────────────────────── -->
          <div class="px-5 py-4 border-b border-n-weak">
            <h2
              class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider mb-4"
            >
              Identity
            </h2>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                  AI Name
                </label>
                <input
                  v-model="form.name"
                  type="text"
                  placeholder="e.g. Priya AI"
                  class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm placeholder:text-n-slate-7 focus:outline-none focus:border-woot-500 focus:ring-1 focus:ring-woot-500/20 transition-colors"
                />
              </div>
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                  Primary Persona
                </label>
                <div class="relative">
                  <select
                    v-model="form.role"
                    class="w-full appearance-none px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm focus:outline-none focus:border-woot-500 cursor-pointer"
                  >
                    <option v-for="r in ROLES" :key="r.value" :value="r.value">
                      {{ r.label }}
                    </option>
                  </select>
                  <span
                    class="i-lucide-chevron-down size-3.5 text-n-slate-8 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none"
                  />
                </div>
                <p class="text-xs text-n-slate-7 mt-1">
                  The AI's primary style and focus. All enabled capabilities
                  below are available regardless of persona.
                </p>
              </div>
            </div>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                  Tone
                </label>
                <div class="relative">
                  <select
                    v-model="form.tone"
                    class="w-full appearance-none px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm focus:outline-none focus:border-woot-500 cursor-pointer capitalize"
                  >
                    <option
                      v-for="tone in TONES"
                      :key="tone"
                      :value="tone"
                      class="capitalize"
                    >
                      {{ tone }}
                    </option>
                  </select>
                  <span
                    class="i-lucide-chevron-down size-3.5 text-n-slate-8 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none"
                  />
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                  Language Mode
                </label>
                <div class="relative">
                  <select
                    v-model="form.language_mode"
                    class="w-full appearance-none px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm focus:outline-none focus:border-woot-500 cursor-pointer"
                  >
                    <option
                      v-for="lm in LANGUAGE_MODES"
                      :key="lm.value"
                      :value="lm.value"
                    >
                      {{ lm.label }}
                    </option>
                  </select>
                  <span
                    class="i-lucide-chevron-down size-3.5 text-n-slate-8 absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none"
                  />
                </div>
              </div>
            </div>
          </div>

          <!-- ── Customer-Facing Identity ─────────────────────── -->
          <div class="px-5 py-4 border-b border-n-weak">
            <h2
              class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider mb-1"
            >
              Customer-Facing Identity
            </h2>
            <p class="text-xs text-n-slate-8 mb-4">
              How the AI introduces itself to customers. Uses
              <strong>Company Name</strong> from NEXCEO Settings → Company
              Knowledge.
            </p>

            <!-- Company name warning -->
            <div
              v-if="!companyName"
              class="flex items-start gap-2 p-3 mb-4 rounded-lg border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-800/10 text-amber-800 dark:text-amber-400 text-xs"
            >
              <span class="i-lucide-alert-triangle size-3.5 shrink-0 mt-0.5" />
              <span>
                <strong>Company name is missing.</strong> Go to NEXCEO Settings
                → Company Knowledge and set <em>Company Name</em> so the AI
                introduces itself correctly instead of saying "NEXCEO".
              </span>
            </div>

            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                  AI Display Name
                </label>
                <input
                  v-model="form.settings.display_name"
                  type="text"
                  :placeholder="form.name || 'e.g. Ananya'"
                  class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm placeholder:text-n-slate-7 focus:outline-none focus:border-woot-500 focus:ring-1 focus:ring-woot-500/20 transition-colors"
                />
                <p class="text-xs text-n-slate-7 mt-1">
                  Name shown to customers. Defaults to AI Name above.
                </p>
              </div>
              <div>
                <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                  Role Title
                </label>
                <input
                  v-model="form.settings.role_title"
                  type="text"
                  placeholder="e.g. Sales Executive"
                  class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm placeholder:text-n-slate-7 focus:outline-none focus:border-woot-500 focus:ring-1 focus:ring-woot-500/20 transition-colors"
                />
                <p class="text-xs text-n-slate-7 mt-1">
                  Customer-facing job title.
                </p>
              </div>
            </div>

            <div class="mb-4">
              <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                Introduction Message Template
              </label>
              <textarea
                v-model="form.settings.intro_message_template"
                rows="2"
                placeholder="Hi, I'm {{ai_name}} from {{company_name}}. I can help you with project details, brochures, and site visit scheduling."
                class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm placeholder:text-n-slate-7 focus:outline-none focus:border-woot-500 focus:ring-1 focus:ring-woot-500/20 transition-colors resize-none"
              />
              <!-- v-pre prevents Vue from trying to interpolate the {{ }} examples -->
              <!-- eslint-disable-next-line vue/no-v-text-v-html-on-component -->
              <p v-pre class="text-xs text-n-slate-7 mt-1">
                Variables: <code>{{ ai_name }}</code
                >, <code>{{ company_name }}</code
                >,
                <code>{{ role_title }}</code>
              </p>
            </div>

            <div class="mb-4">
              <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
                Signature
              </label>
              <input
                v-model="form.settings.signature_template"
                type="text"
                placeholder="{{ai_name}}, {{role_title}}, {{company_name}}"
                class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm placeholder:text-n-slate-7 focus:outline-none focus:border-woot-500 focus:ring-1 focus:ring-woot-500/20 transition-colors"
              />
            </div>

            <!-- Live preview -->
            <div
              class="p-3 rounded-lg border border-n-weak bg-n-alpha-1 text-sm text-n-slate-11"
            >
              <p
                class="text-xs font-medium text-n-slate-9 mb-1.5 uppercase tracking-wider"
              >
                Preview
              </p>
              <p class="italic">{{ identityPreview }}</p>
            </div>
          </div>

          <!-- ── Languages ──────────────────────────────────────── -->
          <div class="px-5 py-4 border-b border-n-weak">
            <h2
              class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider mb-3"
            >
              Languages
            </h2>
            <div class="flex flex-wrap gap-2">
              <button
                v-for="lang in LANGUAGES"
                :key="lang.code"
                class="px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors"
                :class="
                  form.language_preferences.includes(lang.code)
                    ? 'bg-woot-500 text-white border-woot-500'
                    : 'bg-n-background text-n-slate-11 border-n-weak hover:border-woot-400'
                "
                @click="toggleLanguage(lang.code)"
              >
                {{ lang.label }}
              </button>
            </div>
          </div>

          <!-- ── Capabilities ──────────────────────────────────── -->
          <div class="px-5 py-4 border-b border-n-weak">
            <h2
              class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider mb-3"
            >
              Capabilities
            </h2>
            <p class="text-xs text-n-slate-8 mb-3">
              All core capabilities are active for Project AI Employees. These
              become available once the Company AI Coordinator confirms the
              project for a lead.
            </p>
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              <div
                v-for="cap in [
                  {
                    icon: 'i-lucide-check-circle',
                    label: 'Lead qualification & scoring',
                    ok: true,
                  },
                  {
                    icon: 'i-lucide-check-circle',
                    label: 'Project Q&A using approved knowledge',
                    ok: true,
                  },
                  {
                    icon: 'i-lucide-check-circle',
                    label: 'Brochure & document sharing',
                    ok: true,
                  },
                  {
                    icon: 'i-lucide-check-circle',
                    label: 'Inventory queries',
                    ok: true,
                  },
                  {
                    icon: 'i-lucide-check-circle',
                    label: 'Site visit scheduling',
                    ok: true,
                  },
                  {
                    icon: 'i-lucide-check-circle',
                    label: 'Google Meet scheduling',
                    ok: true,
                  },
                  {
                    icon: 'i-lucide-check-circle',
                    label: 'Follow-up automation',
                    ok: true,
                  },
                  {
                    icon: 'i-lucide-check-circle',
                    label: 'Human handoff escalation',
                    ok: true,
                  },
                  {
                    icon: 'i-lucide-check-circle',
                    label: 'Staff alerts & notifications',
                    ok: true,
                  },
                  {
                    icon: 'i-lucide-check-circle',
                    label: 'Kanban stage updates',
                    ok: true,
                  },
                ]"
                :key="cap.label"
                class="flex items-center gap-2 text-xs text-success-700 px-2 py-1 rounded-lg bg-success-50"
              >
                <span
                  class="i-lucide-check-circle size-3 text-success-500 shrink-0"
                />
                {{ cap.label }}
              </div>
            </div>
            <p class="text-xs text-amber-700 flex items-start gap-1.5 mt-3">
              <span class="i-lucide-alert-triangle size-3.5 shrink-0 mt-0.5" />
              Booking confirmation, discounts, legal commitments, and payment
              plans always require human confirmation — the AI will escalate
              these automatically.
            </p>
          </div>

          <!-- ── Permitted Actions ─────────────────────────────── -->
          <div class="px-5 py-4 border-b border-n-weak">
            <div class="flex items-center justify-between mb-1">
              <h2
                class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
              >
                Permitted Actions
              </h2>
              <div class="flex items-center gap-2">
                <span
                  v-if="usingCustomTools"
                  class="text-xs px-1.5 py-0.5 rounded-full bg-woot-50 text-woot-700 dark:bg-woot-500/10 dark:text-woot-400 font-medium"
                >
                  Custom
                </span>
                <button
                  v-if="usingCustomTools"
                  class="text-xs text-n-slate-7 hover:text-ruby-600 hover:underline"
                  @click="resetToRoleDefaults"
                >
                  Reset to defaults
                </button>
              </div>
            </div>
            <!-- Standard capabilities — always enabled for all Project AI Employees -->
            <div class="mb-3">
              <p class="text-xs font-medium text-n-slate-9 mb-1.5">
                Standard Capabilities (always enabled)
              </p>
              <div class="flex flex-wrap gap-1.5">
                <span
                  v-for="cap in AI_CAPABILITIES"
                  :key="cap"
                  class="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium bg-success-50 text-success-700 border border-success-200 dark:bg-success-800/20 dark:text-success-400 dark:border-success-500/30"
                >
                  <span class="i-lucide-check size-2.5" />
                  {{ cap }}
                </span>
              </div>
              <p class="text-xs text-n-slate-7 mt-1.5">
                All capabilities above are active regardless of Primary Persona.
                Primary Persona changes the AI's customer-facing communication
                style only.
              </p>
            </div>

            <p class="text-xs text-n-slate-8 mb-3">
              Advanced tool overrides (optional):
              <span v-if="!useCustomTools"
                >Full tool set active by default.</span
              >
              <span v-else
                >Custom override active — restricts default full-access.</span
              >
            </p>

            <!-- Role-default tools (read-only when not customising) -->
            <div v-if="!useCustomTools" class="flex flex-wrap gap-1.5 mb-3">
              <span
                v-for="tool in effectiveTools"
                :key="tool"
                class="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-mono font-medium bg-n-alpha-2 text-n-slate-10 border border-n-weak"
              >
                <span class="i-lucide-check size-2.5 text-success-500" />
                {{ tool }}
              </span>
            </div>

            <!-- Custom tools checklist (shown when customising) -->
            <div v-else class="grid grid-cols-1 sm:grid-cols-2 gap-1.5 mb-3">
              <label
                v-for="tool in ALL_TOOLS"
                :key="tool"
                class="flex items-center gap-2 text-xs px-2.5 py-1.5 rounded-lg border cursor-pointer transition-colors"
                :class="
                  customToolsList.includes(tool)
                    ? 'border-woot-300 bg-woot-50 text-woot-700 dark:border-woot-500/40 dark:bg-woot-500/10 dark:text-woot-400'
                    : 'border-n-weak text-n-slate-9 hover:border-n-strong'
                "
              >
                <input
                  type="checkbox"
                  class="sr-only"
                  :checked="customToolsList.includes(tool)"
                  @change="toggleTool(tool)"
                />
                <span
                  :class="
                    customToolsList.includes(tool)
                      ? 'i-lucide-check-square text-woot-500'
                      : 'i-lucide-square text-n-slate-6'
                  "
                  class="size-3.5 shrink-0"
                />
                <span class="font-mono">{{ tool }}</span>
              </label>
            </div>

            <!-- Toggle custom mode -->
            <button
              class="text-xs text-woot-500 hover:underline"
              @click="useCustomTools = !useCustomTools"
            >
              {{ useCustomTools ? 'Reset to defaults' : 'Edit permissions' }}
            </button>
          </div>

          <!-- ── Escalation Rules ───────────────────────────────── -->
          <div
            v-if="
              escalationTriggers.length || form.escalation_rules?.max_ai_replies
            "
            class="px-5 py-4 border-b border-n-weak"
          >
            <h2
              class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider mb-3"
            >
              Escalation Rules
            </h2>
            <div class="space-y-2">
              <div
                v-if="form.escalation_rules?.max_ai_replies"
                class="flex items-center justify-between text-sm"
              >
                <span class="text-n-slate-9">Max AI replies before alert</span>
                <span class="font-medium text-n-slate-12">{{
                  form.escalation_rules.max_ai_replies
                }}</span>
              </div>
              <div
                v-if="form.escalation_rules?.notify_on_score"
                class="flex items-center justify-between text-sm"
              >
                <span class="text-n-slate-9">Notify agent when score ≥</span>
                <span class="font-medium text-n-slate-12">{{
                  form.escalation_rules.notify_on_score
                }}</span>
              </div>
              <div v-if="escalationTriggers.length" class="mt-2">
                <p class="text-xs text-n-slate-8 mb-1.5">
                  Handoff keywords (set on project bootstrap):
                </p>
                <div class="flex flex-wrap gap-1">
                  <span
                    v-for="t in escalationTriggers"
                    :key="t"
                    class="text-xs px-2 py-0.5 rounded-full bg-ruby-50 dark:bg-ruby-500/10 text-ruby-700 dark:text-ruby-400 border border-ruby-200 dark:border-ruby-500/20"
                  >
                    {{ t }}
                  </span>
                </div>
              </div>
            </div>
            <p class="text-xs text-n-slate-7 mt-3">
              To modify escalation rules, update via API or re-bootstrap the
              project.
            </p>
          </div>

          <!-- ── Handoff Triggers ───────────────────────────────── -->
          <div class="px-5 py-4 border-b border-n-weak">
            <div class="flex items-center justify-between mb-3">
              <h2
                class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
              >
                Handoff Triggers
              </h2>
              <button
                class="text-xs text-woot-500 hover:underline"
                @click="addDefaultTriggers"
              >
                + Add defaults
              </button>
            </div>
            <p class="text-xs text-n-slate-8 mb-2">
              One keyword or phrase per line. When customer message contains any
              of these, AI hands off to a human agent.
            </p>
            <textarea
              v-model="handoffTriggersRaw"
              rows="6"
              placeholder="discount&#10;legal&#10;talk to manager&#10;angry&#10;refund"
              class="w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-7 focus:outline-none focus:border-woot-500 resize-none font-mono"
            />
            <div class="flex gap-2 mt-2">
              <input
                v-model="newHandoffTrigger"
                type="text"
                placeholder="Add trigger…"
                class="flex-1 px-3 py-1.5 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 placeholder:text-n-slate-7 focus:outline-none focus:border-woot-500"
                @keyup.enter="addTrigger"
              />
              <button
                class="px-3 py-1.5 text-sm rounded-lg bg-n-alpha-2 text-n-slate-12 border border-n-weak hover:bg-n-alpha-3 transition-colors"
                @click="addTrigger"
              >
                Add
              </button>
            </div>
          </div>

          <!-- ── System Prompt ──────────────────────────────────── -->
          <div class="px-5 py-4 border-b border-n-weak">
            <div class="flex items-center justify-between mb-3">
              <h2
                class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
              >
                System Prompt
              </h2>
              <span class="text-xs text-n-slate-8"
                >Never share with customers</span
              >
            </div>
            <textarea
              v-model="form.system_prompt"
              rows="12"
              placeholder="Enter the AI system prompt. Define how the AI should behave, what it can say, and what it should never say…"
              class="w-full px-3 py-2 rounded-lg border border-n-weak bg-n-background text-n-slate-12 text-sm placeholder:text-n-slate-7 focus:outline-none focus:border-woot-500 focus:ring-1 focus:ring-woot-500/20 resize-none font-mono leading-relaxed transition-colors"
            />
            <p class="mt-2 text-xs text-n-slate-8 leading-relaxed">
              Project data, conversation history, and approved documents are
              injected automatically. This prompt adds extra instructions on
              top.
            </p>
          </div>

          <!-- ── AI Test Panel ─────────────────────────────────── -->
          <!-- eslint-disable vue/no-bare-strings-in-template -->
          <div class="px-5 py-4 border-b border-n-weak">
            <div class="flex items-center gap-2 mb-1">
              <span class="i-lucide-flask-conical size-4 text-woot-500" />
              <h2
                class="text-xs font-semibold text-n-slate-9 uppercase tracking-wider"
              >
                Test AI Conversation
              </h2>
              <span
                class="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-200"
              >
                Not sent to customers
              </span>
            </div>
            <p class="text-xs text-n-slate-8 mb-3">
              Simulate a customer message. Save settings first to test with
              latest changes.
            </p>

            <!-- Sample message buttons -->
            <div class="flex flex-wrap gap-1.5 mb-3">
              <button
                v-for="s in SAMPLE_MESSAGES"
                :key="s.label"
                class="text-xs px-2.5 py-1 rounded-full border border-n-weak text-n-slate-9 hover:bg-n-alpha-2 hover:text-n-slate-12 transition-colors"
                @click="useSample(s.message)"
              >
                {{ s.label }}
              </button>
            </div>

            <!-- Input row -->
            <div class="flex gap-2 mb-3">
              <input
                v-model="testInput"
                type="text"
                placeholder='e.g. "Please send the brochure for a 3BHK"'
                class="flex-1 px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-background text-n-slate-12 outline-none focus:border-woot-500"
                @keydown.enter="runTest"
              />
              <button
                :disabled="isTesting || !testInput.trim()"
                class="flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg bg-woot-500 text-white hover:bg-woot-600 disabled:opacity-50 transition-colors"
                @click="runTest"
              >
                <span
                  v-if="isTesting"
                  class="i-lucide-loader-2 size-4 animate-spin"
                />
                <span v-else class="i-lucide-play size-4" />
                Test
              </button>
            </div>

            <!-- Error -->
            <div
              v-if="testError"
              class="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mb-2"
            >
              {{ testError }}
            </div>

            <!-- Results -->
            <div v-if="testResult" class="space-y-3">
              <!-- AI Reply -->
              <div class="rounded-xl border border-n-weak bg-n-alpha-1 p-4">
                <p
                  class="text-xs font-semibold text-n-slate-8 uppercase tracking-wider mb-2"
                >
                  AI Reply
                </p>
                <p
                  class="text-sm text-n-slate-12 whitespace-pre-wrap leading-relaxed"
                >
                  {{ testResult.reply }}
                </p>
              </div>

              <!-- Proposed actions -->
              <div
                v-if="testResult.actions?.length"
                class="rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-900/10 dark:border-amber-700/40 p-4"
              >
                <p
                  class="text-xs font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-wider mb-2"
                >
                  Proposed Actions
                </p>
                <div
                  v-for="(action, i) in testResult.actions"
                  :key="i"
                  class="flex items-start gap-2 text-xs text-amber-800 dark:text-amber-300 mb-1.5"
                >
                  <span
                    class="i-lucide-zap size-3.5 shrink-0 mt-0.5 text-amber-500"
                  />
                  <span>
                    <strong class="font-semibold">{{ action.type }}</strong>
                    <!-- eslint-disable-next-line vue/max-attributes-per-line -->
                    <span v-if="action.stage_key" class="ml-1 text-amber-700"
                      >→ {{ action.stage_key }}</span
                    >
                    <span
                      v-if="action.document_type"
                      class="ml-1 text-amber-700"
                      >→ {{ action.document_type }}</span
                    >
                    <!-- eslint-disable-next-line vue/max-attributes-per-line -->
                    <span v-if="action.scheduled_at" class="ml-1 text-amber-700"
                      >→ {{ action.scheduled_at }}</span
                    >
                    <span
                      v-if="action.reason"
                      class="ml-1 text-amber-600 italic"
                      >({{ action.reason }})</span
                    >
                  </span>
                </div>
              </div>
              <p v-else class="text-xs text-n-slate-7">No actions proposed.</p>

              <!-- Document QA preview -->
              <div
                v-if="testResult.document_preview"
                class="rounded-xl border p-4"
                :class="
                  testResult.document_preview.share_mode === 'direct_share'
                    ? 'border-success-200 bg-success-50 dark:bg-success-800/10 dark:border-success-700/40'
                    : testResult.document_preview.share_mode ===
                        'pending_approval'
                      ? 'border-amber-200 bg-amber-50 dark:bg-amber-900/10 dark:border-amber-700/40'
                      : 'border-red-200 bg-red-50 dark:bg-red-900/10 dark:border-red-700/40'
                "
              >
                <p
                  class="text-xs font-semibold uppercase tracking-wider mb-2"
                  :class="
                    testResult.document_preview.share_mode === 'direct_share'
                      ? 'text-success-700'
                      : testResult.document_preview.share_mode ===
                          'pending_approval'
                        ? 'text-amber-700'
                        : 'text-red-700'
                  "
                >
                  <span class="i-lucide-file-text size-3.5 mr-1" />
                  Document Sharing QA
                </p>
                <div class="space-y-1 text-xs">
                  <div v-if="testResult.document_preview.found">
                    <p class="text-n-slate-11">
                      <span class="font-medium text-n-slate-9">Title:</span>
                      {{ testResult.document_preview.title }}
                    </p>
                    <p class="text-n-slate-11">
                      <span class="font-medium text-n-slate-9">Type:</span>
                      {{ testResult.document_preview.doc_type }}
                    </p>
                    <p>
                      <span class="font-medium text-n-slate-9">Sensitive:</span>
                      <span
                        :class="
                          testResult.document_preview.sensitive
                            ? 'text-red-600'
                            : 'text-success-700'
                        "
                      >
                        {{
                          testResult.document_preview.sensitive ? 'Yes' : 'No'
                        }}
                      </span>
                    </p>
                    <p>
                      <span class="font-medium text-n-slate-9"
                        >Requires Approval:</span
                      >
                      <span
                        :class="
                          testResult.document_preview.requires_approval
                            ? 'text-amber-600'
                            : 'text-success-700'
                        "
                      >
                        {{
                          testResult.document_preview.requires_approval
                            ? 'Yes'
                            : 'No'
                        }}
                      </span>
                    </p>
                    <p
                      class="font-semibold mt-1"
                      :class="
                        testResult.document_preview.share_mode ===
                        'direct_share'
                          ? 'text-success-700'
                          : 'text-amber-700'
                      "
                    >
                      Mode:
                      {{
                        testResult.document_preview.share_mode ===
                        'direct_share'
                          ? '✓ Direct share to customer'
                          : '⚠ Queued for human approval'
                      }}
                    </p>
                  </div>
                  <p v-else class="text-red-700">
                    {{ testResult.document_preview.note }}
                  </p>
                </div>
              </div>

              <!-- System prompt (collapsible) -->
              <div class="rounded-xl border border-n-weak overflow-hidden">
                <button
                  class="w-full flex items-center justify-between px-4 py-2.5 text-xs text-n-slate-9 hover:bg-n-alpha-1 transition-colors"
                  @click="showSystemPrompt = !showSystemPrompt"
                >
                  <span class="flex items-center gap-1.5 font-medium">
                    <span class="i-lucide-code-2 size-3.5" />
                    System Prompt
                  </span>
                  <span
                    :class="
                      showSystemPrompt
                        ? 'i-lucide-chevron-up'
                        : 'i-lucide-chevron-down'
                    "
                    class="size-3.5"
                  />
                </button>
                <div
                  v-if="showSystemPrompt"
                  class="border-t border-n-weak bg-n-alpha-1 px-4 py-3"
                >
                  <pre
                    class="text-xs text-n-slate-10 whitespace-pre-wrap font-mono leading-relaxed overflow-x-auto max-h-96"
                    >{{ testResult.system_prompt }}</pre
                  >
                </div>
              </div>
            </div>
          </div>
          <!-- eslint-enable vue/no-bare-strings-in-template -->

          <!-- ── Actions ────────────────────────────────────────── -->
          <div class="px-5 py-4 flex items-center gap-3">
            <button
              class="flex items-center gap-2 px-5 py-2 rounded-lg bg-woot-500 text-white text-sm font-medium hover:bg-woot-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              :disabled="isSaving"
              @click="save"
            >
              <span
                v-if="isSaving"
                class="i-lucide-loader-2 size-4 animate-spin"
              />
              {{ 'Save Settings' }}
            </button>
            <button
              class="px-5 py-2 rounded-lg border border-n-weak text-n-slate-12 text-sm font-medium hover:bg-n-alpha-2 transition-colors"
              @click="back"
            >
              {{ 'Back to Project' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </ProjectWorkspaceLayout>
</template>
